import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ConfigService } from '../../shared-services/config.service';
import { MatMenuModule } from '@angular/material/menu';
import { debounceTime, EMPTY, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { formatDisplayText } from '../../utils/text-case.utils';

@Component({
  selector: 'lib-drill-down',
  imports: [
    NgxDatatableModule,
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './drill-down.component.html',
  styleUrl: './drill-down.component.scss',
})
export class DrillDownComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: any;
  grandTotalRow: any;
  filter: any;
  previouSelection = '';
  @Input() set widgetId(val: string) {
    this._widgetId = val;
  }
  get widgetId(): string {
    return this._widgetId || this.data?.widgetId;
  }
  protected _widgetId!: string;

  /** Row property holding each node's display text — defaults to `name`
   *  (the legacy shape); pass `data.nameProp = 'label'` for consumers whose
   *  backend uses that key instead (e.g. the DVM Layout Builder). */
  get nameProp(): string {
    return this.data?.nameProp || 'name';
  }

  /** Round-off precision for number cells + the Total row. Pass
   *  `data.decimals` (the Format popup's "Decimal places") to override the
   *  legacy whole-number default. */
  get numberDecimals(): number {
    const raw = Number(this.data?.decimals);
    return Number.isFinite(raw) ? Math.max(0, Math.min(6, Math.floor(raw))) : 0;
  }

  /** Angular number-pipe digitsInfo for the number cells (e.g. "1.2-2"). */
  get numberDigitsInfo(): string {
    const d = this.numberDecimals;
    return `1.${d}-${d}`;
  }

  // ── Visual / reporting capabilities (all config-driven via `data`) ──────────
  // Opt-IN, matching every other toolbar flag below. It used to be opt-out
  // (`!== false`), which put Expand all / Collapse all on every drill-down table
  // whether or not anyone asked for them.
  get enableExpandCollapseAll(): boolean { return this.data?.enableExpandCollapseAll === true; }
  get enableExport(): boolean { return this.data?.enableExport === true; }
  get enableExcelExport(): boolean { return this.data?.enableExcelExport === true; }
  get enablePdfExport(): boolean { return this.data?.enablePdfExport === true; }
  get enablePrint(): boolean { return this.data?.enablePrint === true; }
  get enableSavedViews(): boolean { return this.data?.enableSavedViews === true; }
  get showToolbar(): boolean {
    return this.enableExpandCollapseAll || this.enableExport || this.enableExcelExport
      || this.enablePdfExport || this.enablePrint || this.enableSavedViews;
  }
  /** Conditional-format / data-bar / heatmap rules, keyed by column prop. */
  get visualRules(): Record<string, any> { return (this.data?.visualRules as Record<string, any>) || {}; }
  private get titleText(): string { return String(this.data?.title || 'drill-down'); }

  /** The min/max of a numeric column across the CURRENT rows — memoized per
   *  render so data-bars / heat-maps scale to the visible data. */
  private columnRangeCache: Record<string, { min: number; max: number }> = {};
  private computeColumnRanges(): void {
    this.columnRangeCache = {};
    const props = Object.keys(this.visualRules);
    if (!props.length) return;
    props.forEach((prop) => {
      let min = Infinity, max = -Infinity;
      this.flattenedRows.forEach((r) => {
        if (r?.isTotal) return;
        const n = Number(r?.[prop]);
        if (Number.isFinite(n)) { if (n < min) min = n; if (n > max) max = n; }
      });
      if (min !== Infinity) this.columnRangeCache[prop] = { min, max };
    });
  }
  /** 0..1 position of a value within its column range (for bar width / heat). */
  private normValue(prop: string, value: any): number {
    const range = this.columnRangeCache[prop];
    const n = Number(value);
    if (!range || !Number.isFinite(n) || range.max === range.min) return 0;
    return Math.max(0, Math.min(1, (n - range.min) / (range.max - range.min)));
  }

  /** Which visual a column uses ('databar'|'progress'|'heatmap'|'conditional'|
   *  'kpi'|'badge'|'trend'|'sparkline'|'icon'), from config. */
  visualKind(prop: string): string { return String(this.visualRules[prop]?.kind || ''); }

  /** Inline background color for a heat-map / conditional cell (or ''). */
  cellBg(prop: string, value: any): string {
    const rule = this.visualRules[prop];
    if (!rule) return '';
    if (rule.kind === 'heatmap') {
      const t = this.normValue(prop, value);
      const from = rule.from || [239, 246, 255], to = rule.to || [37, 99, 235];
      const mix = (i: number) => Math.round(from[i] + (to[i] - from[i]) * t);
      return `rgba(${mix(0)}, ${mix(1)}, ${mix(2)}, ${rule.alpha ?? 0.85})`;
    }
    if (rule.kind === 'conditional') {
      const m = this.matchCondition(rule, Number(value));
      return m?.background || '';
    }
    return '';
  }
  cellFg(prop: string, value: any): string {
    const rule = this.visualRules[prop];
    if (rule?.kind === 'conditional') return this.matchCondition(rule, Number(value))?.color || '';
    return '';
  }
  /** Data-bar / progress width (%) for a cell. */
  barWidth(prop: string, value: any): number {
    const rule = this.visualRules[prop];
    if (rule?.kind === 'progress') { const n = Number(value); return Math.max(0, Math.min(100, n)); }
    return Math.round(this.normValue(prop, value) * 100);
  }
  barColor(prop: string): string { return String(this.visualRules[prop]?.color || '#3b82f6'); }

  /** First matching conditional rule for a value. */
  private matchCondition(rule: any, n: number): any {
    if (!Number.isFinite(n) || !Array.isArray(rule?.rules)) return null;
    for (const r of rule.rules) {
      const v = Number(r.value), v2 = Number(r.value2);
      const ok =
        (r.op === '>' && n > v) || (r.op === '>=' && n >= v) ||
        (r.op === '<' && n < v) || (r.op === '<=' && n <= v) ||
        (r.op === '=' && n === v) || (r.op === '!=' && n !== v) ||
        (r.op === 'between' && n >= v && n <= v2);
      if (ok) return r;
    }
    return null;
  }
  /** KPI / status-badge label + class for a cell. */
  badgeFor(prop: string, value: any): { text: string; cls: string } | null {
    const rule = this.visualRules[prop];
    if (rule?.kind !== 'kpi' && rule?.kind !== 'badge') return null;
    const m = this.matchCondition(rule, Number(value));
    return m ? { text: m.label || String(value), cls: m.badgeClass || 'tm-badge-neutral' } : { text: String(value ?? ''), cls: 'tm-badge-neutral' };
  }
  /** Trend arrow + class for a value (positive/negative/flat). */
  trendFor(value: any): { icon: string; cls: string } {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return { icon: '–', cls: 'tm-trend-flat' };
    return n > 0 ? { icon: '▲', cls: 'tm-trend-up' } : { icon: '▼', cls: 'tm-trend-down' };
  }
  /** Build an SVG polyline points string for a sparkline from an array cell. */
  sparkPoints(value: any): string {
    const arr = Array.isArray(value) ? value.map(Number).filter(Number.isFinite) : [];
    if (arr.length < 2) return '';
    const w = 60, h = 18, min = Math.min(...arr), max = Math.max(...arr), span = max - min || 1;
    return arr.map((v, i) => `${(i / (arr.length - 1)) * w},${h - ((v - min) / span) * h}`).join(' ');
  }
  sparkBars(value: any): Array<{ x: number; h: number; w: number }> {
    const arr = Array.isArray(value) ? value.map(Number).filter(Number.isFinite) : [];
    if (!arr.length) return [];
    const w = 60, h = 18, max = Math.max(...arr, 1), bw = w / arr.length;
    return arr.map((v, i) => ({ x: i * bw, h: Math.max(1, (v / max) * h), w: Math.max(1, bw - 1) }));
  }

  flattenedRows: any[] = [];
  visibleColumns: any[] = [];
  totalColumnWidth = 0;
  rawData: any;
  showTotalLevels: number[] = [];
  protected filterSub!: Subscription;

  /** Exact rendered pixel width of each header column, measured straight from
   *  the DOM (see measureColumnWidths). The sticky total row copies these
   *  directly instead of trying to re-derive ngx-datatable's own column-width
   *  algorithm (which changes under columnMode 'force' vs 'standard', on
   *  resize, per column-count, etc.) - copying the real measured value is the
   *  only way to guarantee the total row's dividers land exactly on the real
   *  column boundaries, in every mode, every time. */
  columnWidths: number[] = [];
  private resizeObserver?: ResizeObserver;

  @ViewChild('treeCell', { static: true }) treeCell!: TemplateRef<any>;
  @ViewChild('percentageCell', { static: true })
  percentageCell!: TemplateRef<any>;
  @ViewChild('numberCell', { static: true }) numberCell!: TemplateRef<any>;
  @ViewChild('visualCell', { static: true }) visualCell!: TemplateRef<any>;
  @ViewChild('infoIconHeader', { static: true }) infoIconHeader!: TemplateRef<any>;

  selectedCell: any;
  isLoading = true;
  highlightedCells: any = [];
  customerSpec: any = [];
  customerNameKeys: Set<string> = new Set();
  engineModelKeys: Set<string> = new Set();
  customerSpecKeys: Set<string> = new Set();
  protected unSubscribe$: Subject<void> = new Subject();
  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
  ) {}

  @ViewChild('table', { static: true }) table: any;
  @ViewChild('totalRow', { static: true }) totalRow!: ElementRef<HTMLDivElement>;
  /** Data loading runs here, NOT in ngAfterViewInit: `renderResult` fills
   *  `flattenedRows`, and filling it after the first check is NG0100
   *  ("Previous value: '[]'"). ngAfterViewInit keeps only view measurement. */
  ngOnInit(): void {
    // Pre-fetched data path: when the host has ALREADY called the backend and
    // just wants this component to render the response (e.g. the DVM Layout
    // Builder / published dashboard), render it directly and do not subscribe
    // to global filters. Otherwise a non-target DVM input change can make this
    // table self-reload and blank itself outside the published target filter.
    if (this.data?.staticData) {
      this.renderResult(this.data.staticData);
      return;
    }
    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters) => {
          this.filter = filters;

          // reset the drilldown on reset button click
          if (!filters || Object.keys(filters).length === 0) {
            if (!this.data.filterListner || this.data.filterListner.length === 0) {
              this.isLoading = true;
              return this.loadAndRenderChart(filters);
            }
            //  Reset handling
            this.customerNameKeys.clear();
            this.engineModelKeys.clear();
            this.customerSpecKeys.clear();
            this.highlightedCells = [];
            this.customerSpec = [];

            if (this.rawData) {
              this.rawData.forEach((node: any) => (node.expanded = false));
              this.flattenTree(this.rawData);
              this.updateVisibleColumns();
            }

            return EMPTY;
          }

          if (filters?.widgetId === this.widgetId) {
            return EMPTY;
          }
          this.isLoading = true;
          return this.loadAndRenderChart(filters);
        }),
      )

      .subscribe({
        next: (res: any) => this.renderResult(res),
        error: () => {
          this.isLoading = false;
        },
      });
  }

  ngAfterViewInit(): void {
    this.table.recalculate();
    this.refreshColumnWidths();

    // Re-measure whenever the table's own host element resizes (panel resize,
    // sidebar toggle, browser resize, etc.) - this is what actually keeps the
    // total row's column widths correct on a "responsive" layout, rather than
    // a one-shot delayed guess.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.refreshColumnWidths());
      this.resizeObserver.observe(this.table.element);
    }

    setTimeout(() => {
      // Re-measure now that the surrounding layout (card/panel sizing, our
      // now-content-sized wrapper, etc.) has actually settled - calling
      // recalculate() only once, synchronously in ngAfterViewInit, can catch
      // the host at a stale/narrower width and never re-stretch columns
      // under columnMode: 'force', leaving an unnecessary horizontal
      // scrollbar for columns that would otherwise fit.
      this.refreshColumnWidths();
      const body = this.table.element.querySelector('.datatable-body');
      if (body && this.totalRow) {
        body.addEventListener('scroll', () => {
          this.totalRow.nativeElement.scrollLeft = body.scrollLeft;
        });
      }
    }, 500);
  }

  /** Re-render when the host swaps in new pre-fetched data (e.g. the DVM Layout
   *  Builder updating a tile after an input-control recompute). Without this the
   *  rows are only built once in the initial render, so a data refresh updated
   *  the headers but left the table showing "No data to display". First change
   *  is handled by the initial render; skip it here. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange && this.data?.staticData) {
      this.renderResult(this.data.staticData);
    }
  }

  /** Reshape + render a getConfig response. Accepts either casing — some
   *  backends respond with `Data`/`Total`, others (e.g. the DVM Layout
   *  Builder's analytics engine) with lowercase `data`/`total`. Also accepts
   *  the kaveri-lite drill-down engine's shape, where the grand total isn't a
   *  separate key at all - it's an extra node embedded at the end of `data`
   *  (`isGrandTotal: true`, `level: 0`, `measures: {...}`), which we pull out
   *  here so it never renders as a normal tree row. */
  protected renderResult(res: any): void {
    const rawRows: any[] = res?.Data ?? res?.data ?? [];
    // Clone before flattenTree mutates nodes with a `parent` back-reference —
    // without this, that circular reference lands on the SAME objects the host
    // widget config holds (e.g. the published dashboard's `pageContent`), which
    // later breaks `JSON.stringify` (used by HttpClient to serialize the body)
    // with "Converting circular structure to JSON" when that config is saved/published.
    const rows: any[] = Array.isArray(rawRows) ? this.cloneRows(rawRows) : rawRows;
    const embeddedGrandTotal = Array.isArray(rows) ? rows.find((node: any) => node?.isGrandTotal) : undefined;
    this.rawData = Array.isArray(rows) ? rows.filter((node: any) => !node?.isGrandTotal) : rows;
    this.applyTopLevelGroupOrder(this.rawData);

    const total = res?.Total ?? res?.total ?? embeddedGrandTotal?.measures;
    this.showTotalLevels = this.getShowTotalLevels();
    this.grandTotalRow = total ? this.setTotal(total, 0) : undefined;
    if (this.enableSavedViews) this.restoreView();
    this.flattenTree(this.rawData);
    this.computeColumnRanges();
    this.updateVisibleColumns();
    this.isLoading = false;
    this.refreshColumnWidths();
  }

  /** Fixed top-level display order for the "Options Breakdown" widget's
   *  "Type Grouping" rows - Base Options first, then Additional Options,
   *  then Routing Cost - matching the same order the "valueOrder" dimension
   *  config already declares for the backend. Applied client-side (stable
   *  sort) so the UI shows this order regardless of what the backend
   *  actually returns. Any group name not in this list keeps its existing
   *  relative position after the ones that are, so this is a no-op for
   *  every OTHER drill-down table's groups. */
  private static readonly TOP_LEVEL_GROUP_ORDER = ['base options', 'additional options', 'routing cost'];
  private applyTopLevelGroupOrder(rows: any[]): void {
    if (!Array.isArray(rows) || rows.length < 2) return;
    const rank = (row: any): number => {
      const name = String(row?.[this.nameProp] ?? '').trim().toLowerCase();
      const index = DrillDownComponent.TOP_LEVEL_GROUP_ORDER.indexOf(name);
      return index === -1 ? DrillDownComponent.TOP_LEVEL_GROUP_ORDER.length : index;
    };
    rows.sort((a, b) => rank(a) - rank(b));
  }

  protected loadAndRenderChart(filters?: any): any {
    this.highlightedCells = [];
    this.customerSpec = [];
    this.customerNameKeys.clear();
    this.engineModelKeys.clear();
    this.customerSpecKeys.clear();
    return this.configService
      .getConfig(this.data.apiUrl, this.data, {
        ...this.filter,
        useQueryParams: this.data?.useQueryParams || false,
      })
      .pipe(takeUntil(this.unSubscribe$));
  }
  toggleExpand(row: any): void {
    row.expanded = !row.expanded;
    this.flattenTree(this.rawData);
    this.updateVisibleColumns();
    this.updateHighlightedCells();
    this.refreshColumnWidths();
  }

  /** Expand or collapse EVERY node in the tree (Expand/Collapse All). */
  setAllExpanded(expanded: boolean): void {
    const walk = (nodes: any[]): void => {
      (nodes || []).forEach((n) => { if (n.children?.length) { n.expanded = expanded; walk(n.children); } });
    };
    walk(this.rawData || []);
    this.flattenTree(this.rawData);
    this.updateVisibleColumns();
    this.refreshColumnWidths();
  }

  // ── Export / print (client-side) ────────────────────────────────────────────
  /** Flatten the currently-visible rows into a [headers, ...rows] matrix. */
  private exportMatrix(): { header: string[]; rows: string[][] } {
    const cols = this.visibleColumns;
    const header = cols.map((c) => c.name);
    const rows = this.flattenedRows.map((r) =>
      cols.map((c) => {
        const v = r[c.prop];
        // Tree column: indent by level so the hierarchy survives the export.
        if (c.levelIndex !== undefined || c.indentByLevel) return `${'  '.repeat(Math.max(0, (r.level || 1) - 1))}${r[this.nameProp] ?? ''}`;
        return v === null || v === undefined ? '' : String(v);
      }),
    );
    return { header, rows };
  }
  exportCsv(): void {
    const { header, rows } = this.exportMatrix();
    const esc = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
    const csv = [header.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${this.titleText}.csv`; a.click();
    URL.revokeObjectURL(url);
  }
  async exportExcel(): Promise<void> {
    const { header, rows } = this.exportMatrix();
    try {
      const XLSX: any = await import('xlsx');
      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Drill-Down');
      XLSX.writeFile(wb, `${this.titleText}.xlsx`);
    } catch { this.exportCsv(); }
  }
  /** PDF via the browser's print-to-PDF on a clean printable window. */
  exportPdf(): void { this.printTable(true); }
  printTable(_pdf = false): void {
    const { header, rows } = this.exportMatrix();
    const win = window.open('', '_blank');
    if (!win) return;
    const thead = `<tr>${header.map((h) => `<th>${h}</th>`).join('')}</tr>`;
    const tbody = rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('');
    win.document.write(`<html><head><title>${this.titleText}</title><style>
      body{font:12px system-ui;padding:16px} table{border-collapse:collapse;width:100%}
      th,td{border:1px solid #cbd5e1;padding:4px 8px;text-align:left} th{background:#f1f5f9}
    </style></head><body><h3>${this.titleText}</h3><table>${thead}${tbody}</table></body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  }

  // ── Saved views (localStorage: expand state + sort) ─────────────────────────
  private savedViewKey(): string { return `dvm.dd.view.${this.widgetId || this.titleText}`; }
  saveView(): void {
    try {
      const expandedPaths: string[] = [];
      const walk = (nodes: any[], path: string): void => (nodes || []).forEach((n) => {
        const p = `${path}/${n[this.nameProp] ?? ''}`;
        if (n.expanded) expandedPaths.push(p);
        if (n.children) walk(n.children, p);
      });
      walk(this.rawData || [], '');
      localStorage.setItem(this.savedViewKey(), JSON.stringify({ expandedPaths }));
    } catch { /* storage disabled */ }
  }
  private restoreView(): void {
    try {
      const raw = localStorage.getItem(this.savedViewKey());
      if (!raw) return;
      const { expandedPaths } = JSON.parse(raw) as { expandedPaths: string[] };
      const set = new Set(expandedPaths);
      const walk = (nodes: any[], path: string): void => (nodes || []).forEach((n) => {
        const p = `${path}/${n[this.nameProp] ?? ''}`;
        if (set.has(p) && n.children?.length) n.expanded = true;
        if (n.children) walk(n.children, p);
      });
      walk(this.rawData || [], '');
    } catch { /* ignore */ }
  }

  /** Re-runs ngx-datatable's own layout pass, then (after Angular finishes
   *  applying whatever DOM changes that triggered, e.g. columns array
   *  changing) copies the REAL rendered header-cell widths into
   *  columnWidths - see the field's doc comment for why this is measured
   *  rather than computed/guessed. */
  private refreshColumnWidths(): void {
    this.table?.recalculate();
    setTimeout(() => this.measureColumnWidths());
  }

  /** ngx-datatable's own column-drag-resize event (bound via `(resize)` on
   *  the table) - the header/body cells resize live through its internal
   *  logic, but our separately-measured `columnWidths` (used to size the
   *  custom sticky Total row) only re-measures on a container-level resize,
   *  data reload, or row expand/collapse otherwise. Without this, dragging a
   *  column's own divider left the Total row's cell widths stale, so its
   *  divisions no longer lined up with the just-resized column. No
   *  `recalculate()` here (unlike refreshColumnWidths) - ngx-datatable has
   *  already applied the new widths itself; we only need to re-read them. */
  onColumnResize(): void {
    setTimeout(() => this.measureColumnWidths());
  }

  private measureColumnWidths(): void {
    const host: HTMLElement | undefined = this.table?.element;
    if (!host) return;
    const headerCells = host.querySelectorAll<HTMLElement>(
      '.datatable-header .datatable-header-cell',
    );
    if (!headerCells.length) return;
    this.columnWidths = Array.from(headerCells).map((cell) => cell.offsetWidth);
  }

  /** Deep-clones incoming rows (nested `children` included) so `flattenTree`'s
   *  `node.parent = parent` mutation lands on OUR OWN copy, not the shared
   *  object the host widget config holds. Plain data at this point (no
   *  `parent` back-references yet), so a structured clone is always safe here. */
  private cloneRows(rows: any[]): any[] {
    if (typeof structuredClone === 'function') return structuredClone(rows);
    return JSON.parse(JSON.stringify(rows));
  }

  flattenTree(data: any[]): void {
    const result: any[] = [];
    const traverse = (nodes: any[], parent: any = null): void => {
      for (const node of nodes) {
        node.parent = parent;
        result.push(node);

        if (node.expanded && node.children) {
          traverse(node.children, node);

          const level = node.children[0]?.level;
          if (this.showTotalLevels.includes(level)) {
            const totalRow = this.setTotal(node, level);
            result.push(totalRow);
          }
        }
      }
    };
    traverse(data);
    this.flattenedRows = result;
  }
  onSort(event: any): void {
    const sortProp = event.sorts[0].prop;
    const dir = event.sorts[0].dir; // 'asc' or 'desc'
    this.sortTree(this.rawData, sortProp, dir);
    this.flattenTree(this.rawData);
  }
  sortTree(data: any[], prop: string, dir: 'asc' | 'desc'): void {
    data.sort((a, b) => {
      const valA = a[prop];
      const valB = b[prop];

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === 'string') {
        return dir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      return dir === 'asc' ? valA - valB : valB - valA;
    });

    for (const item of data) {
      if (item.children) {
        this.sortTree(item.children, prop, dir);
      }
    }
  }

  updateVisibleColumns(): void {
    const expandedLevels = new Set<number>();
    this.flattenedRows.forEach((row) => {
      if (row.expanded) {
        expandedLevels.add(row.level);
      }
    });

    this.visibleColumns = this.data.headers
      .map((header: any, index: number) => {
        if (header.expandabile && index > 0 && !expandedLevels.has(index)) {
          return null;
        }

        // Per UI Standards (Point 4): headers in Capital Case unless overridden
        const headerCase = (this.data?.textCase || 'capital') as any;
        const column: any = {
          name: formatDisplayText(header.name, headerCase),
          prop: header.prop,
          width: header.width || undefined,
          type: header.type || undefined,
          infoIcon: header.infoIcon || false,
          infoText: header.infoText || '',
          alignment: header.alignment || 'start',
          frozenLeft: header.frozenLeft || false,
        };

        // Build CSS classes: alignment + optional partition line divider
        const classes: string[] = [];
        if (header.alignment) {
          classes.push(`text-${header.alignment}`);
        }
        // Table partition lines (UI Standards Point 5)
        if (this.showDivider(header.prop)) {
          classes.push('column-divider');
        }
        if (classes.length > 0) {
          column.headerClass = classes.join(' ');
          column.cellClass = classes.join(' ');
        }

        column.headerTemplate = this.infoIconHeader;

        if (header.expandabile) {
          column.cellTemplate = this.treeCell;
          column.levelIndex = index;
          // Single-column tree mode: every level renders in this one column,
          // indented by depth, instead of one column per level.
          column.indentByLevel = header.indentByLevel || false;
        }

        // A column with a configured visual rule (data bar / heat map / badge /
        // trend / sparkline / …) renders through the rich visual cell; else the
        // plain percentage/number cell.
        if (this.visualKind(header.prop)) {
          column.cellTemplate = this.visualCell;
        } else if (header.type === 'percentage') {
          column.cellTemplate = this.percentageCell;
        } else if (header.type === 'number') {
          column.cellTemplate = this.numberCell;
        }

        return column;
      })
      .filter(Boolean);

    this.totalColumnWidth = this.visibleColumns.reduce((sum, col) => sum + (col.width || 0), 0);
  }

  /** Sizes a total-row cell to the REAL rendered width of the header column
   *  at the same index (see columnWidths/measureColumnWidths) - not a
   *  recomputed approximation of ngx-datatable's own column-width algorithm,
   *  which differs by columnMode, column count, and container size, and is
   *  liable to drift from whatever ngx-datatable is actually doing on
   *  screen. Copying the true measured value is the only way this is
   *  guaranteed to line up, in every mode, on every resize. Falls back to
   *  the configured width only for the brief instant before the first
   *  measurement lands. */
  totalCellStyle(index: number, col: any): { [key: string]: string } {
    const measured = this.columnWidths[index];
    const width = measured != null && measured > 0 ? measured : col?.width;
    return { flex: '0 0 auto', width: width != null ? `${width}px` : 'auto' };
  }

  setTotal(total: any, level: number): any {
    const totalRow: any = {
      name: 'Total',
      [this.nameProp]: 'Total',
      level,
      isTotal: true,
    };

    const numericHeaders = this.data.headers.filter((h: any) => !h.expandabile && !h.showTotal);

    numericHeaders.forEach((h: any) => {
      const prop = h.prop;
      const value = total?.[prop];

      if (value != null && !isNaN(value)) {
        if (h.type === 'number') {
          totalRow[prop] = value.toFixed(this.numberDecimals);
        } else if (h.type === 'percentage') {
          totalRow[prop] = value.toFixed(1);
        } else {
          totalRow[prop] = value;
        }
      } else {
        totalRow[prop] = null;
      }
    });
    return totalRow;
  }

  getShowTotalLevels(): number[] {
    return this.data?.headers
      ?.map((h: any, index: number) => (h.expandabile && index !== 0 ? index + 1 : null))
      ?.filter((i: any) => i !== null);
  }

  toProp(name: string) {
    const header = this.data.headers.find((h: any) => h.name === name);
    return header?.prop;
  }

  toggleSet(set: Set<string>, key: string): void {
    if (set.has(key)) set.delete(key);
    else set.add(key);
  }

  setKey(set: Set<string>, key: string, condition: boolean): void {
    if (condition) set.add(key);
    else set.delete(key);
  }

  toggleArray(arr: string[], value: string): void {
    const index = arr.indexOf(value);
    if (index > -1) arr.splice(index, 1);
    else arr.push(value);
  }

  setArray(arr: string[], value: string, add: boolean): void {
    const index = arr.indexOf(value);
    if (add && index === -1) arr.push(value);
    else if (!add && index > -1) arr.splice(index, 1);
  }

  buildKey(row: any): string {
    const parts = [];
    if (row.parent?.parent) parts.push(row.parent.parent[this.nameProp]); // Customer
    if (row.parent && row.level >= 2) parts.push(row.parent[this.nameProp]); // Engine Model
    parts.push(row[this.nameProp]); // Current row
    return parts.join('|');
  }

  onCellActivate(event: any) {
    if (event.type !== 'click') return;

    const columnName = event.column.name;
    const row = event.row;
    const key = this.buildKey(row);

    // --- CUSTOMER NAME LEVEL ---
    if (columnName === 'Customer Name' && row.level === 1) {
      const ems = row.children || [];

      // Collect all Customer Spec keys under this Customer Name
      const allSpecKeys: string[] = [];
      ems.forEach((em: any) => {
        const emKey = `${key}|${em.name}`;
        (em.children || []).forEach((spec: any) => {
          allSpecKeys.push(`${emKey}|${spec.name}`);
        });
      });

      // If all specs selected -> deselect, else select all
      const allSelected = allSpecKeys.every((specKey) => this.customerSpecKeys.has(specKey));

      this.setKey(this.customerNameKeys, key, !allSelected);

      ems.forEach((em: any) => {
        const emKey = `${key}|${em.name}`;
        this.setKey(this.engineModelKeys, emKey, !allSelected);
        (em.children || []).forEach((spec: any) => {
          const specKey = `${emKey}|${spec.name}`;
          this.setKey(this.customerSpecKeys, specKey, !allSelected);
          this.setArray(this.customerSpec, spec.name, !allSelected);
        });
      });
    }

    // --- ENGINE MODEL LEVEL ---
    if (columnName === 'Engine Model' && row.level === 2) {
      const specs = row.children || [];
      const specKeys = specs.map((s: any) => `${key}|${s.name}`);

      // If all specs selected -> deselect, else select all
      const allSelected = specKeys.every((k: string) => this.customerSpecKeys.has(k));

      this.setKey(this.engineModelKeys, key, !allSelected);

      specKeys.forEach((specKey: string, i: number) => {
        this.setKey(this.customerSpecKeys, specKey, !allSelected);
        this.setArray(this.customerSpec, specs[i].name, !allSelected);
      });

      // Update Customer Name selection based on all Engine Models
      const parentKey = this.buildKey(row.parent);
      const allEMSelected = row.parent.children.every((em: any) =>
        this.engineModelKeys.has(`${parentKey}|${em.name}`),
      );
      this.setKey(this.customerNameKeys, parentKey, allEMSelected);
    }

    // --- CUSTOMER SPEC LEVEL ---
    if (columnName === 'Customer Spec' && row.level === 3) {
      this.toggleSet(this.customerSpecKeys, key);
      this.toggleArray(this.customerSpec, row.name);

      const emKey = this.buildKey(row.parent);
      const allSpecsSelected = row.parent.children.every((spec: any) =>
        this.customerSpecKeys.has(`${emKey}|${spec.name}`),
      );
      this.setKey(this.engineModelKeys, emKey, allSpecsSelected);

      const custKey = this.buildKey(row.parent?.parent);
      const allEMSelected = row.parent.parent.children.every((em: any) =>
        this.engineModelKeys.has(`${custKey}|${em.name}`),
      );
      this.setKey(this.customerNameKeys, custKey, allEMSelected);
    }

    // Refresh visual highlights
    this.updateHighlightedCells();

    // Update global filter for customer spec
    this.globalFilterService.updateFilterSelection(
      'customer_spec',
      this.customerSpec,
      false,
      this.widgetId,
      true,
    );
  }

  updateHighlightedCells(): void {
    this.highlightedCells = [];
    this.flattenedRows.forEach((row, idx) => {
      const key = this.buildKey(row);
      if (
        (row.level === 1 && this.customerNameKeys.has(key)) ||
        (row.level === 2 && this.engineModelKeys.has(key)) ||
        (row.level === 3 && this.customerSpecKeys.has(key))
      ) {
        this.highlightedCells.push(`${idx}-name`);
      }
    });
  }

  hasNavigation(levelIndex: number): boolean {
    return this.data.navigation?.some((nav: any) => nav.level === levelIndex);
  }

  onNavigate(navItem: any, row: any): void {
    const queryParams: any = {};
    if (row?.name && navItem.param) {
      queryParams[navItem.param] = row.name;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const path = navItem.path.startsWith('/') ? navItem.path.substring(1) : navItem.path;
    const fullUrl = `${location.origin}/${path}?${queryString}`;
    window.open(fullUrl, '_blank');
  }

  /**
   * Configurable column partition lines (UI Standards Point 5).
   *   data.showColumnDividers = true        → show on ALL columns
   *   data.dividerColumns = ['col1']        → show only after specified columns
   */
  showDivider(columnProp: string): boolean {
    const selective = this.data?.dividerColumns;
    if (Array.isArray(selective) && selective.length > 0) {
      return selective.includes(columnProp);
    }
    return this.data?.showColumnDividers === true;
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}

